import { BlockProps, BlockTransitionType } from '@/components/Block/Block';
import EmojiText from '@/components/EmojiText/EmojiText';
import Chip, { Chips } from '@/components/Redactie/Chip/Chip';
import P from '@/components/Redactie/P/P';
import CHARACTERS from '@/resources/Characters';
import getReadingTime from '@/utils/getReadingTime';
import { mdiThumbUp, mdiThumbUpOutline } from '@mdi/js';
import Image from 'next/image';
import {
    Fragment,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import useLocalStorage from '@/hooks/useLocalStorage';
import { motion, useReducedMotion } from 'framer-motion';
import { DotPulse } from '@uiball/loaders';
import BlockEditorContainer from '@/components/Block/BlockEditorContainer';

import styles from './ChatTextBlock.module.sass';
import Icon from '@mdi/react';
import { useBlockRemoteUserData } from '@/hooks/useBlockRemoteUserData';
import BlockEditorCharacterPicker from '../../BlockEditorCharacterPicker';

interface ChatTextBlockData {
    character: string;
    text: string;
}

const ChatTextBlock = ({
    id,
    data,
    uuid,
    isEditable,
    isAnimated,
    isFirstNonEditorOnlyBlock,
    activeChatCharacter,
    nextBlockTransitionType,
    onContentHeightChanged,
    onFinished,
    onEdited,
}: BlockProps) => {
    const [blockData, setBlockData] = useState<ChatTextBlockData>({
        character:
            data.character ??
            activeChatCharacter?.name ??
            Object.keys(CHARACTERS)[0],
        text:
            data.text?.trim() ?? 'Pudding pie cupcake tiramisu chupa chups 🍭',
    });

    // Get from local storage if user prefers increased text size
    const [isIncreasedTextSizeEnabledLocalStorage] = useLocalStorage(
        'isIncreasedTextSizeEnabled',
        false,
    );

    // Enable increased text size from preferences or when the text is very short
    const isIncreasedTextSizeEnabled =
        isIncreasedTextSizeEnabledLocalStorage || blockData.text.length < 3;

    const readingTimeInSeconds = Math.round(
        getReadingTime(blockData.text) / 1000,
    );

    if (isEditable) {
        return (
            <ChatBlockEditor
                uuid={uuid}
                blockData={blockData}
                setBlockData={setBlockData}
                readingTimeInSeconds={readingTimeInSeconds + 1}
                onEdited={onEdited}
            />
        );
    } else if (isFirstNonEditorOnlyBlock) {
        return (
            <ChatBlockViewerPost
                id={id}
                blockData={blockData}
                isAnimated={isAnimated}
                readingTimeInSeconds={readingTimeInSeconds}
                isIncreasedTextSizeEnabled={isIncreasedTextSizeEnabled}
                nextBlockTransitionType={nextBlockTransitionType}
                onFinished={onFinished}
            />
        );
    } else {
        return (
            <ChatBlockViewerMessage
                uuid={uuid}
                blockData={blockData}
                isAnimated={isAnimated}
                readingTimeInSeconds={readingTimeInSeconds}
                isIncreasedTextSizeEnabled={isIncreasedTextSizeEnabled}
                nextBlockTransitionType={nextBlockTransitionType}
                onContentHeightChanged={onContentHeightChanged}
                onFinished={onFinished}
            />
        );
    }
};

const ChatBlockEditor = ({
    uuid,
    blockData,
    setBlockData,
    readingTimeInSeconds,
    onEdited,
}: {
    uuid: BlockProps['uuid'];
    blockData: ChatTextBlockData;
    setBlockData: React.Dispatch<React.SetStateAction<ChatTextBlockData>>;
    readingTimeInSeconds: number;
    onEdited: BlockProps['onEdited'];
}) => {
    useEffect(() => {
        onEdited?.({
            uuid,
            data: JSON.parse(JSON.stringify(blockData)),
            attachments: {},
        });
    }, [blockData]);

    return (
        <BlockEditorContainer
            className={`bg-${
                CHARACTERS[blockData.character].color
            } bg-opacity-50`}
        >
            <div className="flex gap-2">
                <BlockEditorCharacterPicker
                    characterName={blockData.character}
                    setCharacterName={(id) => {
                        setBlockData({ ...blockData, character: id });
                    }}
                />
                <div className="grow grid gap-2">
                    <P
                        className="w-full"
                        isEditable
                        placeholder={
                            CHARACTERS[blockData.character].displayName
                        }
                        value={blockData.text}
                        onChange={(value) => {
                            setBlockData({ ...blockData, text: value });
                        }}
                        maxLength={255}
                    />
                    <Chips>
                        <Chip
                            className={`${
                                readingTimeInSeconds <= 10
                                    ? `bg-${
                                          CHARACTERS[blockData.character].color
                                      }`
                                    : 'bg-red-500'
                            } text-white`}
                        >
                            Minimaal {readingTimeInSeconds}s leestijd
                        </Chip>
                    </Chips>
                </div>
            </div>
        </BlockEditorContainer>
    );
};

export const ChatBlockProfilePicture = ({
    className = '',
    name,
    isTyping,
}: {
    className?: string;
    name: string;
    isTyping: boolean;
}) => (
    <div
        className={`${styles['chattextblock__avatar']} ${
            isTyping ? styles['chattextblock__avatar--typing'] : ''
        } ${className}`}
    >
        <div className={styles['chattextblock__avatar__cropcontainer']}>
            <Image
                className={
                    styles['chattextblock__avatar__cropcontainer__image']
                }
                src={require(`@/resources/CharacterAvatars/${name}.jpg`)}
                width={128}
                height={128}
                alt={`Profielfoto van ${CHARACTERS[name].displayName}`}
                aria-hidden="true"
            />
        </div>
    </div>
);

export const ChatBlockTypingIndicator = ({
    layoutId,
    className,
    displayName,
    isAnimated,
    onClick,
}: {
    layoutId?: string;
    className?: string;
    displayName: string;
    isAnimated: BlockProps['isAnimated'];
    onClick: () => void;
}) => {
    const shouldReduceMotion = useReducedMotion();

    return (
        <motion.button
            layoutId={isAnimated && !shouldReduceMotion ? layoutId : undefined}
            className={`${styles['chattextblock__bubble']} ${styles['chattextblock__bubble--typing']} ${className}`}
            whileTap={{
                scale: !shouldReduceMotion ? 0.9 : 1,
            }}
            whileHover={{
                scale: !shouldReduceMotion ? 1.1 : 1,
            }}
            onClick={onClick}
            title="Open bericht"
        >
            <span className="sr-only">{`${displayName} is aan het typen`}</span>
            <DotPulse color="currentColor" size={25} />
        </motion.button>
    );
};

const ChatBlockViewerMessage = ({
    uuid,
    blockData,
    isAnimated,
    nextBlockTransitionType,
    readingTimeInSeconds,
    isIncreasedTextSizeEnabled,
    onFinished,
    onContentHeightChanged,
}: {
    uuid: BlockProps['uuid'];
    blockData: ChatTextBlockData;
    isAnimated: BlockProps['isAnimated'];
    nextBlockTransitionType: BlockProps['nextBlockTransitionType'];
    readingTimeInSeconds: number;
    isIncreasedTextSizeEnabled: boolean;
    onFinished: BlockProps['onFinished'];
    onContentHeightChanged: BlockProps['onContentHeightChanged'];
}) => {
    const chatTextBlockRef = useRef(null);
    const shouldReduceMotion = useReducedMotion();

    const [isTyping, setIsTyping] = useState(isAnimated);

    // Calculate and execute the typing and reading time
    useEffect(() => {
        setTimeout(
            () => {
                if (chatTextBlockRef.current) {
                    setIsTyping(false);
                }
            },
            blockData.text.length > 12 ? 3500 : 1500,
        );
        setTimeout(
            () => {
                if (chatTextBlockRef.current) onFinished?.();
            },
            (nextBlockTransitionType === BlockTransitionType.AUTOMATIC
                ? readingTimeInSeconds * 1000
                : 1500) + 3000,
        );
    }, []);

    // Notify that the content height changed when the typing animation is finished, so the timeline scrolls down
    useLayoutEffect(() => {
        if (chatTextBlockRef.current) {
            onContentHeightChanged?.();
        }
    }, [isTyping]);

    return (
        <div
            ref={chatTextBlockRef}
            className={`${styles['chattextblock']} ${
                isAnimated ? styles['chattextblock--animated'] : ''
            } ${
                isIncreasedTextSizeEnabled
                    ? styles['chattextblock--increasedtextsize']
                    : ''
            }`}
        >
            <ChatBlockProfilePicture
                name={blockData.character}
                isTyping={!!isTyping}
            />
            {isTyping ? (
                <ChatBlockTypingIndicator
                    layoutId={`chattextblock__bubble-${uuid}`}
                    displayName={CHARACTERS[blockData.character].displayName}
                    isAnimated={isAnimated}
                    onClick={() => {
                        setIsTyping(false);
                    }}
                />
            ) : (
                <motion.div
                    layoutId={
                        isAnimated && !shouldReduceMotion
                            ? `chattextblock__bubble-${uuid}`
                            : undefined
                    }
                    layoutDependency={isTyping}
                    className={`${styles['chattextblock__bubble']} bg-${
                        CHARACTERS[blockData.character].color
                    }`}
                    transition={{
                        duration: 0.3,
                        type: 'spring',
                        bounce: 0.1,
                    }}
                >
                    <p className={styles['chattextblock__bubble__text']}>
                        <span
                            className={
                                styles['chattextblock__bubble__text__name']
                            }
                        >
                            {CHARACTERS[blockData.character].displayName}
                            <span className="sr-only"> zegt:</span>
                        </span>
                        <span
                            className={
                                styles['chattextblock__bubble__text__message']
                            }
                        >
                            {blockData.text
                                .trim()
                                .split(/\r?\n/)
                                .map((line, i) => (
                                    <Fragment key={i}>
                                        <EmojiText>{line}</EmojiText>
                                        <br />
                                    </Fragment>
                                ))}
                        </span>
                    </p>
                </motion.div>
            )}
        </div>
    );
};

const ChatBlockViewerPost = ({
    id,
    blockData,
    isAnimated,
    nextBlockTransitionType,
    readingTimeInSeconds,
    isIncreasedTextSizeEnabled,
    onFinished,
}: {
    id: BlockProps['id'];
    blockData: ChatTextBlockData;
    isAnimated: BlockProps['isAnimated'];
    nextBlockTransitionType: BlockProps['nextBlockTransitionType'];
    readingTimeInSeconds: number;
    isIncreasedTextSizeEnabled: boolean;
    onFinished: BlockProps['onFinished'];
}) => {
    const chatTextBlockRef = useRef(null);
    const shouldReduceMotion = useReducedMotion();

    // Store likes
    const { userDataRemote, submitUserDataRemote } = useBlockRemoteUserData(id);

    // Calculate and execute the typing and reading time
    useEffect(() => {
        setTimeout(
            () => {
                if (chatTextBlockRef.current) onFinished?.();
            },
            (nextBlockTransitionType === BlockTransitionType.AUTOMATIC
                ? readingTimeInSeconds * 1000
                : 1500) + 3000,
        );
    }, []);

    const likeCount = useMemo(() => {
        if (userDataRemote?.summary?.length === 0) return 0;
        // Get the count out of the object in the summary array where the data is true
        return (
            userDataRemote?.summary?.find((summary) => summary.data === true)
                ?.count ?? 0
        );
    }, [userDataRemote]);

    return (
        <div
            className={`group ${styles['chattextblock']} ${
                styles['chattextblock--post']
            } ${isAnimated ? styles['chattextblock--animated'] : ''} ${
                isIncreasedTextSizeEnabled
                    ? styles['chattextblock--increasedtextsize']
                    : ''
            } bg-${CHARACTERS[blockData.character].color}`}
        >
            <div
                ref={chatTextBlockRef}
                className={`${styles['chattextblock--post__header']}`}
            >
                <ChatBlockProfilePicture
                    name={blockData.character}
                    isTyping={false}
                />
                <div
                    className={`${styles['chattextblock--post__header__data']}`}
                >
                    <span
                        className={`${styles['chattextblock--post__header__data__name']}`}
                    >
                        {CHARACTERS[blockData.character].displayName}
                    </span>
                    <span
                        className={`${styles['chattextblock--post__header__data__time']} opacity-75 group-hover:opacity-100 group-focus-within:opacity-100`}
                    >
                        <span className="sm:hidden">heeft geplaatst</span>
                        <span className="hidden sm:inline-block">
                            heeft zojuist geplaatst
                        </span>
                    </span>
                </div>
                <motion.button
                    title={`${likeCount} vinden dit leuk`}
                    aria-label="Leuk vinden"
                    aria-pressed={!!userDataRemote?.submitted_data}
                    className={`${
                        styles['chattextblock--post__header__likebutton']
                    } ${
                        userDataRemote?.submitted_data
                            ? `${
                                  styles[
                                      'chattextblock--post__header__likebutton--liked'
                                  ]
                              } text-${CHARACTERS[blockData.character].color} ${
                                  likeCount > 0
                                      ? styles[
                                            'chattextblock--post__header__likebutton--hascount'
                                        ]
                                      : ''
                              } text-${CHARACTERS[blockData.character].color}`
                            : styles[
                                  'chattextblock--post__header__likebutton--default'
                              ]
                    }`}
                    initial={{
                        opacity: 0,
                        scale: shouldReduceMotion ? 1 : 0,
                    }}
                    animate={{
                        opacity: 1,
                        scale: 1,
                    }}
                    whileHover={{
                        scale: shouldReduceMotion
                            ? 1
                            : !userDataRemote?.submitted_data
                              ? 1.2
                              : 1.1,
                    }}
                    whileTap={{
                        scale: !shouldReduceMotion ? 0.95 : 1,
                    }}
                    onClick={() => {
                        submitUserDataRemote(!userDataRemote?.submitted_data, {
                            optimisticData: {
                                submitted_data: !userDataRemote?.submitted_data,
                                count: likeCount + 1,
                                summary: [
                                    {
                                        data: !userDataRemote?.submitted_data,
                                        count: likeCount + 1,
                                    },
                                ],
                            },
                        });
                    }}
                >
                    <div
                        className={`${
                            styles[
                                'chattextblock--post__header__likebutton__content'
                            ]
                        } ${
                            userDataRemote?.submitted_data
                                ? styles[
                                      'chattextblock--post__header__likebutton__content--liked'
                                  ]
                                : ''
                        }`}
                    >
                        <Icon
                            path={
                                !userDataRemote?.submitted_data
                                    ? mdiThumbUpOutline
                                    : mdiThumbUp
                            }
                            size={0.9}
                        />
                        {!!likeCount && (
                            <span
                                className={
                                    styles[
                                        'chattextblock--post__header__likebutton__content__count'
                                    ]
                                }
                            >
                                {likeCount}
                            </span>
                        )}
                    </div>
                </motion.button>
            </div>
            <div
                ref={chatTextBlockRef}
                className={`${styles['chattextblock--post__content']}`}
            >
                {blockData.text
                    .trim()
                    .split(/\r?\n/)
                    .map((line, i) => (
                        <Fragment key={i}>
                            <EmojiText>{line}</EmojiText>
                            <br />
                        </Fragment>
                    ))}
            </div>
        </div>
    );
};
export default ChatTextBlock;
